'use client';

import React, { Suspense, lazy, useMemo, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import type { ProjectWorkShowcaseImage } from './ProjectWorkShowcase';
import type { ProjectWorkShowcaseLightboxSlide } from './ProjectWorkShowcaseLightbox';

const ProjectWorkShowcaseLightbox = lazy(() => import('./ProjectWorkShowcaseLightbox'));

type ProjectWorkShowcaseGalleryBaseProps = {
    heroImage: ProjectWorkShowcaseImage;
    images: ProjectWorkShowcaseImage[];
    imageFallbackAlt: string;
    lightboxLabel: string;
};

const getImageSrc = (image?: ProjectWorkShowcaseImage) => image?.url || image?.src || image?.original || image?.thumbnail || '';

const getImageAlt = (image: ProjectWorkShowcaseImage, fallback: string) => image.alt || image.title || fallback;

const getImageTitle = (image: ProjectWorkShowcaseImage, fallback: string) =>
    image.caption || image.title || image.description || getImageAlt(image, fallback);

const getImageKey = (image: ProjectWorkShowcaseImage, index: number) =>
    String(image.id || getImageSrc(image) || `${getImageTitle(image, 'Project photo')}-${index}`);

const uniqueImages = (images: ProjectWorkShowcaseImage[]) => {
    const seen = new Set<string>();

    return images.filter((image, index) => {
        const src = getImageSrc(image);
        if (!src) return false;

        const key = src || getImageKey(image, index);
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
    });
};

function ProjectImageButton({
    image,
    index,
    frameClassName,
    imageClassName,
    loading = 'lazy',
    fallbackAlt,
    onOpen,
}: {
    image: ProjectWorkShowcaseImage;
    index: number;
    frameClassName: string;
    imageClassName: string;
    loading?: 'eager' | 'lazy';
    fallbackAlt: string;
    onOpen: (index: number) => void;
}) {
    const src = getImageSrc(image);
    const alt = getImageAlt(image, fallbackAlt);
    const objectPosition = image.objectPosition ? { objectPosition: image.objectPosition } : undefined;

    if (!src) return null;

    return (
        <button
            type="button"
            className={`${frameClassName} project-work-showcase-image-button`}
            aria-label={`Open ${alt} in full screen gallery`}
            title="Open image in full screen"
            onClick={() => onOpen(index)}
        >
            <img
                className={imageClassName}
                src={src}
                alt={alt}
                title={image.title}
                loading={loading}
                decoding="async"
                style={objectPosition}
            />
            <span className="project-work-showcase-image-expand" aria-hidden="true">
                <Maximize2 size={18} strokeWidth={2.4} />
            </span>
        </button>
    );
}

function ProjectLazyLightbox({
    slides,
    activeImageIndex,
    lightboxLabel,
    onClose,
}: {
    slides: ProjectWorkShowcaseLightboxSlide[];
    activeImageIndex: number;
    lightboxLabel: string;
    onClose: () => void;
}) {
    if (activeImageIndex < 0) return null;

    return (
        <Suspense fallback={null}>
            <ProjectWorkShowcaseLightbox
                slides={slides}
                activeImageIndex={activeImageIndex}
                lightboxLabel={lightboxLabel}
                onClose={onClose}
            />
        </Suspense>
    );
}

const getLightboxSlides = (galleryImages: ProjectWorkShowcaseImage[], imageFallbackAlt: string): ProjectWorkShowcaseLightboxSlide[] =>
    galleryImages.map((image) => ({
        src: getImageSrc(image),
        alt: getImageAlt(image, imageFallbackAlt),
        title: image.title,
        description: image.caption || image.description,
        width: image.width,
        height: image.height,
    }));

export function ProjectWorkShowcaseHeroGallery({
    heroImage,
    images,
    imageFallbackAlt,
    lightboxLabel,
}: ProjectWorkShowcaseGalleryBaseProps) {
    const [activeImageIndex, setActiveImageIndex] = useState(-1);
    const galleryImages = useMemo(() => uniqueImages([heroImage, ...(images || [])]), [heroImage, images]);
    const slides = useMemo(() => getLightboxSlides(galleryImages, imageFallbackAlt), [galleryImages, imageFallbackAlt]);
    const heroSrc = getImageSrc(heroImage);
    const heroImageIndex = Math.max(
        0,
        galleryImages.findIndex((image) => getImageSrc(image) === heroSrc),
    );
    const featuredImages = galleryImages.slice(0, 4);

    const openLightbox = (index: number) => {
        if (!galleryImages.length) return;
        setActiveImageIndex(Math.max(0, Math.min(index, galleryImages.length - 1)));
    };

    return (
        <>
            <figure className="project-work-showcase-hero-figure">
                <ProjectImageButton
                    image={heroImage}
                    index={heroImageIndex}
                    frameClassName="project-work-showcase-hero-image-frame"
                    imageClassName="project-work-showcase-hero-image"
                    loading="eager"
                    fallbackAlt={imageFallbackAlt}
                    onOpen={openLightbox}
                />
                <figcaption>{getImageTitle(heroImage, imageFallbackAlt)}</figcaption>
            </figure>

            <div className="project-work-showcase-featured-strip" aria-label="More photos from this project">
                {featuredImages.map((image, index) => (
                    <figure className="project-work-showcase-featured-image-frame" key={getImageKey(image, index)}>
                        <ProjectImageButton
                            image={image}
                            index={index}
                            frameClassName="project-work-showcase-featured-image-wrap"
                            imageClassName="project-work-showcase-featured-image"
                            fallbackAlt={imageFallbackAlt}
                            onOpen={openLightbox}
                        />
                        <figcaption className="project-work-showcase-featured-caption">
                            {getImageTitle(image, imageFallbackAlt)}
                        </figcaption>
                    </figure>
                ))}
            </div>

            <ProjectLazyLightbox
                slides={slides}
                activeImageIndex={activeImageIndex}
                lightboxLabel={lightboxLabel}
                onClose={() => setActiveImageIndex(-1)}
            />
        </>
    );
}

export function ProjectWorkShowcasePhotoGalleryGrid({
    heroImage,
    images,
    imageFallbackAlt,
    lightboxLabel,
}: ProjectWorkShowcaseGalleryBaseProps) {
    const [activeImageIndex, setActiveImageIndex] = useState(-1);
    const galleryImages = useMemo(() => uniqueImages([heroImage, ...(images || [])]), [heroImage, images]);
    const slides = useMemo(() => getLightboxSlides(galleryImages, imageFallbackAlt), [galleryImages, imageFallbackAlt]);

    const openLightbox = (index: number) => {
        if (!galleryImages.length) return;
        setActiveImageIndex(Math.max(0, Math.min(index, galleryImages.length - 1)));
    };

    return (
        <>
            <div className="project-work-showcase-gallery-grid">
                {galleryImages.map((image, index) => (
                    <figure className="project-work-showcase-gallery-item" key={getImageKey(image, index)}>
                        <ProjectImageButton
                            image={image}
                            index={index}
                            frameClassName="project-work-showcase-gallery-image-wrap"
                            imageClassName="project-work-showcase-gallery-image"
                            fallbackAlt={imageFallbackAlt}
                            onOpen={openLightbox}
                        />
                        <figcaption>{getImageTitle(image, imageFallbackAlt)}</figcaption>
                    </figure>
                ))}
            </div>

            <ProjectLazyLightbox
                slides={slides}
                activeImageIndex={activeImageIndex}
                lightboxLabel={lightboxLabel}
                onClose={() => setActiveImageIndex(-1)}
            />
        </>
    );
}
